import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { todayIso } from "@/lib/dates";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Clock, Check, Trash2, FileText, Wrench } from "lucide-react";
import { toast } from "sonner";

// Quick "log hours against this task" dialog — used from the office Job Tasks
// panel AND the field worker's run-sheet. Supports picking multiple machines.
export function LogTaskHoursDialog({ task, onClose, onLogged }) {
  const [hours, setHours] = useState("");
  const [date, setDate] = useState(todayIso());
  const [notes, setNotes] = useState("");
  const [machineIds, setMachineIds] = useState([]);
  const [fleet, setFleet] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/fleet")
      .then((r) => setFleet((r.data || []).filter((v) => v.status === "active")))
      .catch(() => {});
  }, []);

  const toggleMachine = (id) =>
    setMachineIds((m) => (m.includes(id) ? m.filter((x) => x !== id) : [...m, id]));

  const save = async () => {
    const h = Number(hours);
    if (!Number.isFinite(h) || h <= 0) { toast.error("Enter the hours worked on this task"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/job-tasks/${task.id}/log-hours`, {
        hours: h, date, machine_ids: machineIds, notes: notes.trim() || null,
      });
      toast.success(`${h}h logged against "${task.title}"`);
      onLogged?.(data.logged_hours, data.log);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to log hours");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm max-w-md max-h-[85vh] overflow-y-auto" data-testid="log-task-hours-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black uppercase tracking-tight flex items-center gap-2">
            <Clock className="w-5 h-5 text-[#FF5F15]" /> Log task hours
          </DialogTitle>
          <DialogDescription className="text-xs">
            <strong>{task.title}</strong>
            {" — "}{(task.logged_hours || 0).toFixed(1)}h logged so far
            {task.budget_hours ? ` of ${task.budget_hours}h budgeted` : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="label-tag">Hours</Label>
              <Input type="number" step="0.25" min="0" value={hours}
                     onChange={(e) => setHours(e.target.value)} autoFocus
                     placeholder="e.g. 3.5"
                     className="mt-1.5 rounded-sm border-zinc-400"
                     data-testid="log-hours-input" />
            </div>
            <div>
              <Label className="label-tag">Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                     className="mt-1.5 rounded-sm border-zinc-400"
                     data-testid="log-hours-date-input" />
            </div>
          </div>
          {fleet.length > 0 && (
            <div>
              <Label className="label-tag">Machines used <span className="text-zinc-400 normal-case font-normal">(pick any that apply)</span></Label>
              <div className="mt-1.5 flex flex-wrap gap-1.5 max-h-32 overflow-y-auto" data-testid="log-hours-machines">
                {fleet.map((v) => {
                  const on = machineIds.includes(v.id);
                  return (
                    <button key={v.id} type="button" onClick={() => toggleMachine(v.id)}
                            className={`px-2 py-1 text-[10px] font-bold uppercase tracking-wider border-2 transition-colors ${
                              on ? "bg-[#09090B] text-white border-[#09090B]" : "bg-white border-zinc-300 text-zinc-600 hover:border-[#FF5F15]"
                            }`}
                            data-testid={`log-hours-machine-${v.id}`}>
                      {v.rego}{on && <Check className="w-3 h-3 inline ml-1 text-[#FF5F15]" />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div>
            <Label className="label-tag">Notes <span className="text-zinc-400 normal-case font-normal">(optional)</span></Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                      placeholder="e.g. Digger on trench, second half of the day"
                      className="mt-1.5 rounded-sm border-zinc-400 text-sm"
                      data-testid="log-hours-notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm uppercase tracking-wider font-bold text-xs">Cancel</Button>
          <Button onClick={save} disabled={busy}
                  className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-xs"
                  data-testid="log-hours-save-btn">
            {busy ? "Saving…" : "Log hours"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Every hours entry logged against a task — from timesheets and quick logs.
export function TaskHoursDetailDialog({ task, onClose, onChanged }) {
  const { user } = useAuth();
  const isOffice = user.role !== "field";
  const [data, setData] = useState(null);

  const load = () => {
    api.get(`/job-tasks/${task.id}/hours`).then((r) => setData(r.data)).catch(() => {});
  };
  useEffect(load, [task.id]);

  const removeLog = async (log) => {
    if (!window.confirm(`Delete this ${log.hours}h entry?`)) return;
    try {
      await api.delete(`/task-hour-logs/${log.id}`);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const total = data?.total_hours ?? task.logged_hours ?? 0;
  const budget = task.budget_hours || 0;
  const pct = budget ? Math.min(100, Math.round((total / budget) * 100)) : 0;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm max-w-lg max-h-[85vh] overflow-y-auto" data-testid="task-hours-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black uppercase tracking-tight flex items-center gap-2">
            <Clock className="w-5 h-5 text-[#FF5F15]" /> Hours on task
          </DialogTitle>
          <DialogDescription className="text-xs"><strong>{task.title}</strong></DialogDescription>
        </DialogHeader>
        <div className="border-2 border-zinc-900 bg-zinc-50 p-3 flex items-center gap-4">
          <div>
            <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">Total logged</div>
            <div className="font-display font-black text-2xl leading-none mt-0.5" data-testid="task-hours-total">
              {total.toFixed(1)}<span className="text-xs text-zinc-500 ml-0.5">h</span>
            </div>
          </div>
          {budget > 0 && (
            <div className="flex-1">
              <div className="flex items-center justify-between text-[9px] font-bold uppercase tracking-widest text-zinc-500">
                <span>Budget {budget}h</span><span className={total > budget ? "text-red-600" : ""}>{pct}%</span>
              </div>
              <div className="h-2 bg-zinc-200 mt-1">
                <div className={`h-full transition-all ${total > budget ? "bg-red-600" : "bg-[#FF5F15]"}`} style={{ width: `${pct}%` }} />
              </div>
            </div>
          )}
        </div>
        <div className="space-y-1.5 mt-1" data-testid="task-hours-log-list">
          {data && data.items.length === 0 && (
            <div className="text-xs text-zinc-400 italic text-center py-3">No hours logged against this task yet.</div>
          )}
          {(data?.items || []).map((l) => (
            <div key={l.id} className="group flex items-start gap-2.5 border border-zinc-200 bg-white px-2.5 py-2 text-xs" data-testid={`task-hours-log-${l.id}`}>
              <span className="font-mono font-bold text-[#FF5F15] shrink-0 w-12 text-right">{Number(l.hours || 0).toFixed(1)}h</span>
              <div className="min-w-0 flex-1">
                <div className="font-bold">{l.user_name}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">
                  {l.date}
                  {l.source === "timesheet" && <span className="ml-1.5 bg-zinc-100 border border-zinc-300 px-1 py-px">Timesheet</span>}
                </div>
                {l.machine_names?.length > 0 && (
                  <div className="text-[10px] text-zinc-600 mt-0.5 flex items-center gap-1">
                    <Wrench className="w-3 h-3 text-zinc-400" /> {l.machine_names.join(", ")}
                  </div>
                )}
                {l.notes && (
                  <div className="text-[11px] text-zinc-500 mt-0.5 flex items-start gap-1">
                    <FileText className="w-3 h-3 mt-0.5 shrink-0 text-zinc-400" /> {l.notes}
                  </div>
                )}
              </div>
              {l.source !== "timesheet" && (isOffice || l.user_id === user.id) && (
                <button type="button" onClick={() => removeLog(l)}
                        className="opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-red-600 transition-opacity shrink-0"
                        data-testid={`task-hours-log-delete-${l.id}`} aria-label="Delete entry">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
